import type {
  EventDetailResponse,
  EventPlanSummaryResponse,
  EventFacetKey,
  EventLibraryFacets,
  EventLibraryItem,
  EventLibraryResponse,
  EventUploadProvisionResponse,
  EventUploadStatusResponse,
  LiveEventPlaybackResponse,
  OneTimeEventInput,
  OneTimeEventOptionsResponse,
  PostDateEventInput,
  VideoPlaybackResponse,
  VideoCommentsResponse,
  UpdateVideoControlsRequest,
} from "@friends-church-dvr/contracts";
import { isCloudflarePlaybackStorageStatus } from "@friends-church-dvr/contracts";
import { groupEventsByServiceType, groupUpNextAndPast, isFacetInitiallyOpen } from "./event-library-timeline";
import { renderIndexedMedia } from "./indexed-media";
import { adjustStreamPlayback, currentStreamPositionMs, prepareStreamPlayer, seekAndPlayStream, toggleStreamPlayback } from "./cloudflare-stream-player";
import { playbackGestureOffsetMs, playbackShortcutAction, type PlaybackGestureDirection } from "./playback-keyboard";
import { playbackUrlAt } from "./event-playback-url";
import { uploadVideoDirectly } from "./direct-video-upload";
import { responseErrorMessage } from "./request-error";
import { restoreIsActive, restoreProgressPresentation } from "./restore-progress";

interface ShellOptions {
  app: HTMLDivElement;
  brand: string;
  menu: string;
  canRestoreArchived?: boolean;
  canPlayVideos?: boolean;
  canManageVisibility?: boolean;
  canManageRetention?: boolean;
  canManageOneTimeEvents?: boolean;
  canEditEventMetadata?: boolean;
  canManagePlanAssociation?: boolean;
  canAdjustCaptureWindows?: boolean;
  canDeleteEvents?: boolean;
  canEditPhase1bMetadata?: boolean;
  canViewIndexedMedia?: boolean;
  canShareVideo?: boolean;
  canAddVideoComments?: boolean;
  canViewVideoComments?: boolean;
  canViewAnalytics?: boolean;
}

const wireViewingAnalytics = (iframe: HTMLIFrameElement, videoId: string, mode: "live" | "recorded"): void => {
  const player = window.Stream?.(iframe) as unknown as { addEventListener(type: string, listener: () => void): void } | undefined;
  if (!player) return;
  let sessionId: string | null = null;
  let heartbeat: number | null = null;
  const update = (ended = false): void => {
    if (!sessionId) return;
    void fetch(`/api/viewing-sessions/${encodeURIComponent(sessionId)}`, { method: "PATCH", headers: { "Content-Type": "application/json", Origin: window.location.origin }, body: JSON.stringify({ ended }) });
    if (ended) { sessionId = null; if (heartbeat !== null) window.clearInterval(heartbeat); heartbeat = null; }
  };
  const begin = (): void => { void (async () => {
    if (sessionId || document.hidden) return;
    const response = await fetch(`/api/videos/${encodeURIComponent(videoId)}/viewing-sessions`, { method: "POST", headers: { "Content-Type": "application/json", Origin: window.location.origin }, body: JSON.stringify({ mode }) });
    if (!response.ok) return;
    sessionId = String((await response.json() as { sessionId: string }).sessionId);
    heartbeat = window.setInterval(() => { if (!document.hidden) update(); }, 30_000);
  })(); };
  player.addEventListener("playing", begin);
  player.addEventListener("pause", () => update(true));
  player.addEventListener("ended", () => update(true));
  document.addEventListener("visibilitychange", () => { if (document.hidden) update(true); });
  window.addEventListener("pagehide", () => update(true), { once: true });
};

const escapeHtml = (value: string): string =>
  value.replace(/[&<>'"]/g, (character) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;",
  })[character] ?? character);

const formatTime = (value: string): string =>
  new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
    hour12: true,
    timeZone: "America/New_York",
  }).format(new Date(value));

const formatPlanItemLength = (seconds: number | null): string | null => {
  if (seconds === null || seconds <= 0) return null;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return minutes > 0 ? `${minutes}:${String(remainder).padStart(2, "0")}` : `${remainder}s`;
};

const stateLabel = (event: EventLibraryItem): string => {
  if (event.eventStatus === "live") return "Live now";
  if (event.eventStatus === "scheduled" && event.mediaStatus === "waiting") {
    return "This Event Hasn't Started Yet";
  }
  if (event.storageStatus === "archived") return "Archived playback options";
  if (event.mediaStatus === "ready") return "Ready to play";
  if (event.mediaStatus === "failed") return "Processing failed";
  return event.mediaStatus.charAt(0).toUpperCase() + event.mediaStatus.slice(1);
};

const localTime = (value: string): string => new Intl.DateTimeFormat("en-CA", {
  hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: "America/New_York",
}).format(new Date(value));

const optionsMarkup = (items: Array<{ id: string; name: string }>): string =>
  items.map((item) => `<option value="${escapeHtml(item.id)}">${escapeHtml(item.name)}</option>`).join("");

const oneTimeEventForm = (options: OneTimeEventOptionsResponse): string => `
  <dialog class="one-time-event-dialog" id="one-time-event-dialog" aria-labelledby="one-time-event-title">
    <div class="one-time-event-dialog-heading">
      <div><p class="eyebrow">Non-recurring capture</p><h2 id="one-time-event-title">Create One-Time Event</h2></div>
      <button class="one-time-event-dialog-close" id="close-one-time-event" type="button" aria-label="Close one-time Event form">×</button>
    </div>
    <p class="one-time-event-warning">Add a non-recurring Eastern Time capture window. This requires an event to be created in the Resi Encoder otherwise this recording event will fail.</p>
    <form id="one-time-event-form">
      <div class="config-grid two-column">
        <label class="config-field"><span>Title</span><input name="title" required maxlength="200"></label>
        <label class="config-field"><span>Service Type</span><select name="serviceTypeId" required><option value="">Select Service Type</option>${optionsMarkup(options.serviceTypes)}</select></label>
        <label class="config-field"><span>Live Input</span><select name="liveInputId" required><option value="">Select Live Input</option>${optionsMarkup(options.liveInputs)}</select></label>
        <label class="config-field"><span>Event date</span><input name="eventDateLocal" type="date" required></label>
        <label class="config-field"><span>Start time</span><input name="scheduledStartTimeLocal" type="time" required></label>
        <label class="config-field"><span>End time</span><input name="scheduledEndTimeLocal" type="time" required></label>
        <label class="config-field"><span>Pre-roll minutes</span><input name="preRollMinutes" type="number" min="0" max="180" value="0" required></label>
        <label class="config-field"><span>Post-roll minutes</span><input name="postRollMinutes" type="number" min="0" max="180" value="5" required></label>
      </div>
      <label class="config-field"><span>Notes</span><textarea name="notes" rows="3" maxlength="2000"></textarea></label>
      <p class="form-message" role="status" aria-live="polite"></p>
      <button class="primary-button compact" type="submit">Create Event</button>
    </form>
  </dialog>`;

const postDateEventForm = (options: OneTimeEventOptionsResponse): string => `
  <dialog class="one-time-event-dialog" id="post-date-event-dialog" aria-labelledby="post-date-event-title">
    <div class="one-time-event-dialog-heading">
      <div><p class="eyebrow">Recorded outside DVR</p><h2 id="post-date-event-title">Create Post-Date Event</h2></div>
      <button class="one-time-event-dialog-close" id="close-post-date-event" type="button" aria-label="Close post-date Event form">×</button>
    </div>
    <p class="one-time-event-warning">Create an Event that already occurred, then upload its finished video directly to Cloudflare Stream.</p>
    <form id="post-date-event-form">
      <div class="config-grid two-column">
        <label class="config-field"><span>Title</span><input name="title" required maxlength="200"></label>
        <label class="config-field"><span>Service Type</span><select name="serviceTypeId" required><option value="">Select Service Type</option>${optionsMarkup(options.serviceTypes)}</select></label>
        <label class="config-field"><span>Event date</span><input name="eventDateLocal" type="date" required></label>
        <label class="config-field"><span>Start time</span><input name="scheduledStartTimeLocal" type="time" required></label>
        <label class="config-field"><span>End time</span><input name="scheduledEndTimeLocal" type="time" required></label>
        <label class="config-field"><span>Video file</span><input name="video" type="file" accept="video/*,.mkv,.mxf,.gxf,.lxf,.ts" required></label>
      </div>
      <label class="config-field"><span>Notes</span><textarea name="notes" rows="3" maxlength="2000"></textarea></label>
      <progress class="video-upload-progress" value="0" max="100" hidden></progress>
      <p class="form-message" role="status" aria-live="polite"></p>
      <button class="primary-button compact" type="submit">Create and Upload</button>
    </form>
  </dialog>`;

const provisionAndUpload = async (
  eventId: string,
  file: File,
  message: HTMLElement | null,
  progress: HTMLProgressElement | null,
): Promise<EventUploadStatusResponse> => {
  const provisionResponse = await fetch(`/api/events/${encodeURIComponent(eventId)}/upload`, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json", Origin: window.location.origin },
    body: JSON.stringify({ fileName: file.name, fileSizeBytes: file.size, mimeType: file.type || "application/octet-stream" }),
  });
  if (!provisionResponse.ok) throw new Error(await responseErrorMessage(provisionResponse));
  const provision = await provisionResponse.json() as EventUploadProvisionResponse;
  if (progress) { progress.hidden = false; progress.value = 0; }
  await uploadVideoDirectly(file, provision.uploadUrl, (uploaded, total) => {
    const percent = total ? Math.round(uploaded / total * 100) : 0;
    if (progress) progress.value = percent;
    if (message) message.textContent = `Uploading video… ${percent}%`;
  });
  if (message) message.textContent = "Upload complete. Cloudflare is processing the video…";
  const statusResponse = await fetch(`/api/events/${encodeURIComponent(eventId)}/upload/status`, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json", Origin: window.location.origin },
    body: "{}",
  });
  if (!statusResponse.ok) throw new Error(await responseErrorMessage(statusResponse));
  return statusResponse.json() as Promise<EventUploadStatusResponse>;
};

const eventCard = (event: EventLibraryItem): string => `
  <a class="event-card" href="/events/${encodeURIComponent(event.id)}${event.transcriptMatch ? `?t=${event.transcriptMatch.startMs}` : ""}">
    <div class="event-thumbnail${event.thumbnailUrl ? " has-artwork" : ""}" aria-hidden="true">
      <span>${escapeHtml(event.serviceTypeName.slice(0, 2).toUpperCase())}</span>
      ${event.thumbnailUrl ? `<img src="${escapeHtml(event.thumbnailUrl)}" alt="" loading="lazy" decoding="async" referrerpolicy="no-referrer">` : ""}
    </div>
    <div class="event-card-body">
      <p class="eyebrow">${escapeHtml(event.serviceTypeName)}</p>
      <h2>${escapeHtml(event.title)}</h2>
      <p>${escapeHtml(formatTime(event.scheduledStartAt))}</p>
      ${event.transcriptMatch ? `<p class="transcript-match"><strong>${escapeHtml(Math.floor(event.transcriptMatch.startMs / 60_000) + ":" + String(Math.floor(event.transcriptMatch.startMs / 1_000) % 60).padStart(2, "0"))}</strong> ${escapeHtml(event.transcriptMatch.text)}</p>` : ""}
      <span class="event-state ${event.eventStatus}">${escapeHtml(event.isHidden ? "Hidden · " : "")}${escapeHtml(stateLabel(event))}</span>
    </div>
  </a>`;

const eventCarousel = (
  id: string,
  label: string,
  events: EventLibraryItem[],
): string => events.length ? `
  <section class="timeline-group" aria-labelledby="${id}-title">
    <div class="timeline-divider timeline-period-divider" id="${id}-title"><span>${escapeHtml(label)}</span></div>
    <div class="event-carousel" role="region" aria-label="${escapeHtml(label)}" tabindex="0">
      ${events.map(eventCard).join("")}
    </div>
  </section>` : "";

const serviceTypeEventCarousel = (
  label: string,
  events: EventLibraryItem[],
): string => events.length ? `
  <section class="timeline-group">
    <div class="event-carousel" role="region" aria-label="${escapeHtml(label)} Events" tabindex="0">
      ${events.map(eventCard).join("")}
    </div>
  </section>` : "";

const pastEventsSection = (events: EventLibraryItem[]): string => events.length ? `
  <section class="timeline-period-group" aria-labelledby="past-events-title">
    <div class="timeline-divider timeline-period-divider" id="past-events-title"><span>Past Events</span></div>
    <div class="timeline-service-type-groups">
      ${groupEventsByServiceType(events).map((group) =>
        serviceTypeEventCarousel(group.serviceTypeName, group.events)).join("")}
    </div>
  </section>` : "";

const wireThumbnailImageFallbacks = (root: HTMLElement): void => {
  root.addEventListener("error", (event) => {
    const image = event.target;
    if (image instanceof HTMLImageElement && image.matches(".event-thumbnail img")) image.remove();
  }, true);
};

const playbackGestureMarkup = (): string => `
  <div class="playback-gesture-layer" aria-hidden="true">
    <div class="playback-gesture-zone playback-gesture-left" data-playback-gesture="rewind"><span>−10s</span></div>
    <div class="playback-gesture-zone playback-gesture-right" data-playback-gesture="fast-forward"><span>+10s</span></div>
  </div>`;

const wirePlaybackGestures = (player: HTMLElement, iframe: HTMLIFrameElement): void => {
  player.insertAdjacentHTML("beforeend", playbackGestureMarkup());
  player.querySelectorAll<HTMLElement>("[data-playback-gesture]").forEach((zone) => {
    let singleClickTimer: number | null = null;
    let feedbackTimer: number | null = null;
    const focusPlayer = (): void => {
      player.tabIndex = -1;
      player.focus({ preventScroll: true });
    };
    zone.addEventListener("click", (clickEvent) => {
      clickEvent.preventDefault();
      focusPlayer();
      if (clickEvent.detail > 1) {
        if (singleClickTimer !== null) window.clearTimeout(singleClickTimer);
        singleClickTimer = null;
        return;
      }
      singleClickTimer = window.setTimeout(() => {
        singleClickTimer = null;
        void toggleStreamPlayback(iframe).catch(() => undefined);
      }, 280);
    });
    zone.addEventListener("dblclick", (doubleClickEvent) => {
      doubleClickEvent.preventDefault();
      if (singleClickTimer !== null) window.clearTimeout(singleClickTimer);
      singleClickTimer = null;
      focusPlayer();
      const direction = zone.dataset.playbackGesture as PlaybackGestureDirection;
      const offsetMs = playbackGestureOffsetMs(direction);
      void adjustStreamPlayback(iframe, offsetMs).catch(() => undefined);
      zone.classList.remove("playback-gesture-active");
      void zone.offsetWidth;
      zone.classList.add("playback-gesture-active");
      if (feedbackTimer !== null) window.clearTimeout(feedbackTimer);
      feedbackTimer = window.setTimeout(() => zone.classList.remove("playback-gesture-active"), 650);
    });
  });
};

const facetLabels: Record<EventFacetKey, string> = {
  serviceType: "Service Type",
  storage: "Availability",
  series: "Series",
  pastor: "Pastor / Speaker",
  song: "Song",
  songLeader: "Song Leader",
  emcee: "MC / Emcee",
  tag: "Custom Tags",
};

const storageLabels: Record<string, string> = {
  none: "Upcoming / No media",
  online: "Online",
  archive_pending: "Archiving",
  online_archived: "Online + Archived",
  archived: "Archived",
  restore_pending: "Restoring",
};

const facetGroups = (facets: EventLibraryFacets): string =>
  (Object.keys(facetLabels) as EventFacetKey[]).map((key) => {
    const entries = facets[key];
    const providerFacet = ["series", "pastor", "song", "songLeader", "emcee"].includes(key);
    return `
      <details class="facet-group" ${isFacetInitiallyOpen(key) ? "open" : ""}>
        <summary><span>${facetLabels[key]}</span><small>${entries.length}</small></summary>
        <div class="facet-options">
          ${entries.length ? entries.map((entry) => `
            <label class="facet-toggle">
              <input type="checkbox" name="${key}" value="${escapeHtml(entry.value)}">
              <span>${escapeHtml(key === "storage" ? storageLabels[entry.label] ?? entry.label : entry.label)}</span>
              <small>${entry.count}</small>
            </label>`).join("") : `<p class="facet-empty">${providerFacet ? "Available after Planning Center Services data is synchronized." : "No values in your authorized Events."}</p>`}
        </div>
      </details>`;
  }).join("");

export const renderEventLibrary = async (options: ShellOptions): Promise<void> => {
  options.app.innerHTML = `
    <main class="shell library-shell">
      <header class="topbar page-title-topbar">
        ${options.brand}
        <div class="mobile-page-heading" aria-hidden="true">
          <h1>Event library.</h1>
        </div>
        ${options.menu}
      </header>
      <section class="library-intro">
        <div class="library-intro-copy">
          <h1>Event library.</h1>
        </div>
      </section>
      <section class="live-event-section" id="live-event-section" hidden><div class="section-heading"><div><p class="eyebrow">Happening now</p><h2>Live Events</h2></div></div><div class="event-carousel" id="live-event-list" role="region" aria-label="Live Events" tabindex="0"></div></section>
      <form id="library-filters">
        <div class="library-searchbar">
          <label class="library-search-query"><span>Search titles &amp; transcripts</span><input name="q" type="search" maxlength="100" placeholder="Search Events"></label>
          ${options.canManageVisibility ? '<label class="include-hidden-toggle"><input type="checkbox" name="includeHidden" value="true"><span>Include Hidden Videos</span></label>' : ""}
          <label class="library-search-timing"><span>View</span><select name="timing"><option value="all">All Events</option><option value="upcoming">Upcoming</option><option value="past">Past</option></select></label>
          <div class="library-search-actions">
            <button class="primary-button compact" type="submit">Search</button>
            <button class="secondary-button compact" type="reset">Reset</button>
          </div>
        </div>
        <button class="mobile-filter-trigger" id="mobile-filter-trigger" type="button" aria-expanded="false" aria-controls="facet-groups">
          <span>Filters</span><span class="mobile-filter-count" id="mobile-filter-count">0 active</span>
        </button>
        <div class="library-layout">
          <div class="library-filter-column">
            <aside class="facet-panel" id="facet-groups" aria-label="Event filters">
              <div class="facet-panel-toolbar">
                <span>Filter tags</span>
                <button class="facet-clear-button" id="clear-filters" type="reset">Clear filters</button>
              </div>
              <details class="facet-date-range">
                <summary>Date Range</summary>
                <div class="facet-date-range-options">
                  <label><span>From</span><input name="dateFrom" type="date"></label>
                  <label><span>Through</span><input name="dateTo" type="date"></label>
                </div>
              </details>
              <div id="facet-group-list"><p>Loading filters…</p></div>
            </aside>
            ${options.canManageOneTimeEvents ? `<section class="library-event-actions" aria-labelledby="library-event-actions-title">
              <p id="library-event-actions-title">Create an Event</p>
              <button class="secondary-button compact" id="open-post-date-event" type="button">Create Post-Date Event</button>
              <button class="secondary-button compact" id="open-one-time-event" type="button">Create One-Time Event</button>
            </section>
            ` : ""}
          </div>
          <div>
            <div class="library-count" id="library-count">Loading Events…</div>
            <div class="event-results" id="event-list"><div class="event-carousel"><article class="event-card skeleton"></article></div></div>
          </div>
        </div>
      </form>
      <div id="one-time-event-container"></div>
    </main>`;
  wireThumbnailImageFallbacks(options.app);

  const form = document.querySelector<HTMLFormElement>("#library-filters");
  const list = document.querySelector<HTMLElement>("#event-list");
  const count = document.querySelector<HTMLElement>("#library-count");
  const facets = document.querySelector<HTMLElement>("#facet-groups");
  const facetList = document.querySelector<HTMLElement>("#facet-group-list");
  const filterTrigger = document.querySelector<HTMLButtonElement>("#mobile-filter-trigger");
  const filterCount = document.querySelector<HTMLElement>("#mobile-filter-count");
  const liveSection = document.querySelector<HTMLElement>("#live-event-section");
  const liveList = document.querySelector<HTMLElement>("#live-event-list");
  let facetsRendered = false;

  const updateFilterCount = (): void => {
    if (!form || !filterCount) return;
    const data = new FormData(form);
    let active = 0;
    for (const [key, value] of data.entries()) {
      if (key !== "timing" && String(value).trim()) active += 1;
      if (key === "timing" && value !== "all") active += 1;
    }
    filterCount.textContent = `${active} active`;
  };

  filterTrigger?.addEventListener("click", () => {
    if (!facets) return;
    const opening = !facets.classList.contains("mobile-open");
    facets.classList.toggle("mobile-open", opening);
    filterTrigger.setAttribute("aria-expanded", String(opening));
  });

  const load = async (): Promise<void> => {
    if (!form || !list) return;
    const query = new URLSearchParams(new FormData(form) as never);
    for (const [key, value] of [...query]) if (!value) query.delete(key);
    try {
      const response = await fetch(`/api/events?${query}`, { headers: { Accept: "application/json" } });
      if (!response.ok) throw new Error("Events could not be loaded.");
      const payload = (await response.json()) as EventLibraryResponse;
      if (facetList && !facetsRendered) {
        facetList.innerHTML = facetGroups(payload.facets);
        facetsRendered = true;
      }
      const liveEvents = payload.events.filter((event) => event.eventStatus === "live");
      const otherEvents = payload.events.filter((event) => event.eventStatus !== "live");
      if (liveSection && liveList) { liveSection.hidden = liveEvents.length === 0; liveList.innerHTML = liveEvents.map(eventCard).join(""); }
      if (otherEvents.length) {
        const timing = String(new FormData(form).get("timing") ?? "all");
        if (timing === "upcoming") {
          list.innerHTML = eventCarousel("upcoming-events", "Upcoming Events", otherEvents);
        } else if (timing === "past") {
          list.innerHTML = pastEventsSection(otherEvents);
        } else {
          const grouped = groupUpNextAndPast(otherEvents);
          list.innerHTML = `${pastEventsSection(grouped.past)}${eventCarousel("upcoming-events", "Upcoming Events", grouped.upNext)}`;
        }
      } else {
        list.innerHTML = '<p class="empty-state">No Events match these filters.</p>';
      }
      if (count) count.textContent = `${payload.total} Event${payload.total === 1 ? "" : "s"}`;
      updateFilterCount();
    } catch (error) {
      list.innerHTML = `<p class="error-message">${escapeHtml(error instanceof Error ? error.message : "Events could not be loaded.")}</p>`;
    }
  };

  form?.addEventListener("submit", (event) => { event.preventDefault(); void load(); });
  form?.addEventListener("change", (event) => {
    updateFilterCount();
    if ((event.target as HTMLElement).matches('.facet-toggle input')) void load();
  });
  form?.addEventListener("reset", () => setTimeout(() => {
    updateFilterCount();
    void load();
  }, 0));
  if (options.canManageOneTimeEvents) {
    const container = document.querySelector<HTMLElement>("#one-time-event-container");
    try {
      const response = await fetch("/api/events/options", { headers: { Accept: "application/json" } });
      if (!response.ok) throw new Error("One-time Event options could not be loaded.");
      const eventOptions = await response.json() as OneTimeEventOptionsResponse;
      if (container) container.innerHTML = `${oneTimeEventForm(eventOptions)}${postDateEventForm(eventOptions)}`;
      const dialog = document.querySelector<HTMLDialogElement>("#one-time-event-dialog");
      const postDateDialog = document.querySelector<HTMLDialogElement>("#post-date-event-dialog");
      document.querySelector<HTMLButtonElement>("#open-one-time-event")?.addEventListener("click", () => {
        dialog?.showModal();
      });
      document.querySelector<HTMLButtonElement>("#close-one-time-event")?.addEventListener("click", () => {
        dialog?.close();
      });
      document.querySelector<HTMLButtonElement>("#open-post-date-event")?.addEventListener("click", () => {
        postDateDialog?.showModal();
      });
      document.querySelector<HTMLButtonElement>("#close-post-date-event")?.addEventListener("click", () => {
        postDateDialog?.close();
      });
      const createForm = document.querySelector<HTMLFormElement>("#one-time-event-form");
      createForm?.addEventListener("submit", (event) => {
        event.preventDefault(); void (async () => {
          const message = createForm.querySelector<HTMLElement>(".form-message");
          const button = createForm.querySelector<HTMLButtonElement>('button[type="submit"]');
          const data = new FormData(createForm);
          const body: OneTimeEventInput = {
            serviceTypeId: String(data.get("serviceTypeId")), liveInputId: String(data.get("liveInputId")), title: String(data.get("title")),
            eventDateLocal: String(data.get("eventDateLocal")), scheduledStartTimeLocal: String(data.get("scheduledStartTimeLocal")), scheduledEndTimeLocal: String(data.get("scheduledEndTimeLocal")),
            preRollMinutes: Number(data.get("preRollMinutes")), postRollMinutes: Number(data.get("postRollMinutes")), notes: String(data.get("notes")).trim() || null,
          };
          try {
            if (button) { button.disabled = true; button.textContent = "Creating…"; }
            const response = await fetch("/api/events", { method: "POST", headers: { Accept: "application/json", "Content-Type": "application/json", Origin: window.location.origin }, body: JSON.stringify(body) });
            if (!response.ok) throw new Error("The one-time Event could not be created.");
            const payload = await response.json() as EventDetailResponse;
            window.location.assign(`/events/${encodeURIComponent(payload.event.id)}`);
          } catch (error) {
            if (button) { button.disabled = false; button.textContent = "Create Event"; }
            if (message) message.textContent = error instanceof Error ? error.message : "The one-time Event could not be created.";
          }
        })();
      });
      const postDateForm = document.querySelector<HTMLFormElement>("#post-date-event-form");
      let createdPostDateEventId: string | null = null;
      postDateForm?.addEventListener("submit", (event) => {
        event.preventDefault(); void (async () => {
          const data = new FormData(postDateForm);
          const message = postDateForm.querySelector<HTMLElement>(".form-message");
          const button = postDateForm.querySelector<HTMLButtonElement>('button[type="submit"]');
          const progress = postDateForm.querySelector<HTMLProgressElement>("progress");
          const fileInput = postDateForm.elements.namedItem("video") as HTMLInputElement;
          const file = fileInput.files?.[0];
          if (!file) { if (message) message.textContent = "Choose a video file."; return; }
          const body: PostDateEventInput = {
            serviceTypeId: String(data.get("serviceTypeId")), title: String(data.get("title")),
            eventDateLocal: String(data.get("eventDateLocal")), scheduledStartTimeLocal: String(data.get("scheduledStartTimeLocal")),
            scheduledEndTimeLocal: String(data.get("scheduledEndTimeLocal")), notes: String(data.get("notes")).trim() || null,
          };
          try {
            if (button) { button.disabled = true; button.textContent = createdPostDateEventId ? "Retrying…" : "Creating…"; }
            if (!createdPostDateEventId) {
              if (message) message.textContent = "Creating post-date Event…";
              const createResponse = await fetch("/api/events/post-date", {
                method: "POST",
                headers: { Accept: "application/json", "Content-Type": "application/json", Origin: window.location.origin },
                body: JSON.stringify(body),
              });
              if (!createResponse.ok) throw new Error(await responseErrorMessage(createResponse));
              const created = await createResponse.json() as EventDetailResponse;
              createdPostDateEventId = created.event.id;
            }
            if (button) button.textContent = "Uploading…";
            await provisionAndUpload(createdPostDateEventId, file, message, progress);
            window.location.assign(`/events/${encodeURIComponent(createdPostDateEventId)}`);
          } catch (error) {
            if (button) { button.disabled = false; button.textContent = createdPostDateEventId ? "Retry Upload" : "Create and Upload"; }
            if (message) {
              const detail = error instanceof Error ? error.message : "The post-date Event upload failed.";
              message.textContent = createdPostDateEventId
                ? `The Event was created, but its upload did not start. ${detail} Select Retry Upload to try again without creating another Event.`
                : detail;
              message.className = "form-message error-message";
            }
          }
        })();
      });
    } catch (error) { if (container) container.innerHTML = `<p class="error-message">${escapeHtml(error instanceof Error ? error.message : "One-time Event options could not be loaded.")}</p>`; }
  }
  await load();
};

export const renderEventPlanSummary = async (
  options: ShellOptions,
  eventId: string,
): Promise<void> => {
  options.app.innerHTML = `
    <main class="shell library-shell plan-summary-shell">
      <section class="event-detail plan-summary" id="plan-summary"><p>Loading Plan Summary…</p></section>
    </main>
  `;
  const summary = document.querySelector<HTMLElement>("#plan-summary");
  if (!summary) return;
  const wireCloseButton = (): void => {
    document.querySelector<HTMLButtonElement>("#close-plan-summary")?.addEventListener("click", () => {
      window.close();
      window.setTimeout(() => {
        if (!window.closed) window.history.back();
      }, 100);
    });
  };
  try {
    const response = await fetch(`/api/events/${encodeURIComponent(eventId)}/plan-summary`, {
      headers: { Accept: "application/json" },
    });
    if (!response.ok) {
      throw new Error(response.status === 404
        ? "A Planning Center Plan is not available for this Event."
        : "The Plan Summary could not be loaded.");
    }
    const payload = await response.json() as EventPlanSummaryResponse;
    const peopleByTeam = new Map<string, typeof payload.plan.people>();
    for (const person of payload.plan.people) {
      const team = person.team?.trim() || "Unassigned";
      const members = peopleByTeam.get(team) ?? [];
      members.push(person);
      peopleByTeam.set(team, members);
    }
    const people = [...peopleByTeam.entries()].sort(([first], [second]) => first.localeCompare(second)).map(([team, members]) =>
      `<section class="plan-summary-team"><h4>${escapeHtml(team)}</h4><ul>${members.map((person) =>
        `<li><strong>${escapeHtml(person.name)}</strong>${person.position ? `<small>${escapeHtml(person.position)}</small>` : ""}</li>`,
      ).join("")}</ul></section>`,
    ).join("");
    const items = payload.plan.items.map((item) => {
      const length = formatPlanItemLength(item.lengthSeconds);
      const assigned = item.assignedPeople.map((person) =>
        `<span>${escapeHtml(person.name)}${person.position ? ` · ${escapeHtml(person.position)}` : ""}</span>`,
      ).join("");
      return `<li class="plan-summary-item${item.itemType?.trim().toLocaleLowerCase("en-US") === "header" ? " plan-summary-header" : ""}">
        <div class="plan-summary-item-heading">
          <strong>${escapeHtml(item.title)}</strong>
          ${length ? `<time>${escapeHtml(length)}</time>` : ""}
        </div>
        ${item.itemType ? `<small class="plan-summary-item-type">${escapeHtml(item.itemType)}</small>` : ""}
        ${item.description ? `<p>${escapeHtml(item.description)}</p>` : ""}
        ${assigned ? `<div class="plan-summary-assignees">${assigned}</div>` : ""}
      </li>`;
    }).join("");
    summary.innerHTML = `
      <div class="plan-summary-close-row"><button class="secondary-button compact plan-summary-close" id="close-plan-summary" type="button">Close</button></div>
      <p class="eyebrow">Read-only Plan Summary</p>
      <h1 class="event-detail-title">${escapeHtml(payload.event.title)}</h1>
      <p class="lede">${escapeHtml(payload.event.serviceTypeName)} · ${escapeHtml(formatTime(payload.event.scheduledStartAt))}</p>
      <section class="plan-summary-panel">
        <header>
          <h2>${escapeHtml(payload.event.planLabel)}</h2>
          ${payload.plan.seriesTitle ? `<p>${escapeHtml(payload.plan.seriesTitle)}</p>` : ""}
          ${payload.plan.dateLabel ? `<small>${escapeHtml(payload.plan.dateLabel)}</small>` : ""}
        </header>
        ${people ? `<section><h3>People</h3><div class="plan-summary-teams">${people}</div></section>` : ""}
        <section><h3>Service plan</h3>${items
          ? `<ol class="plan-summary-items">${items}</ol>`
          : "<p class=\"form-message\">No plan items have been synchronized yet.</p>"}</section>
      </section>
    `;
    wireCloseButton();
  } catch (error) {
    summary.innerHTML = `<div class="plan-summary-close-row"><button class="secondary-button compact plan-summary-close" id="close-plan-summary" type="button">Close</button></div><p class="error-message">${escapeHtml(error instanceof Error ? error.message : "The Plan Summary could not be loaded.")}</p>`;
    wireCloseButton();
  }
};

export const renderEventDetail = async (
  options: ShellOptions,
  eventId: string,
): Promise<void> => {
  const requestedStartValue = new URLSearchParams(window.location.search).get("t");
  const requestedStartMs = requestedStartValue && /^\d{1,8}$/u.test(requestedStartValue)
    ? Math.min(Number(requestedStartValue), 86_400_000)
    : 0;
  let timestampPlaybackShortcutEnabled = false;
  const playbackShortcutHandler = (keyboardEvent: KeyboardEvent): void => {
    const target = keyboardEvent.target;
    const hasInteractiveTarget = target instanceof Element && Boolean(target.closest(
      'input, textarea, select, button, a[href], summary, [contenteditable="true"], [role="button"]',
    ));
    if (!timestampPlaybackShortcutEnabled) return;
    const action = playbackShortcutAction({
      key: keyboardEvent.key,
      code: keyboardEvent.code,
      repeat: keyboardEvent.repeat,
      altKey: keyboardEvent.altKey,
      ctrlKey: keyboardEvent.ctrlKey,
      metaKey: keyboardEvent.metaKey,
      shiftKey: keyboardEvent.shiftKey,
      hasInteractiveTarget,
    });
    if (!action) return;
    const iframe = document.querySelector<HTMLIFrameElement>(".stream-player iframe");
    const nativeVideo = document.querySelector<HTMLVideoElement>("#archive-instant-video");
    if (!iframe && !nativeVideo) return;
    keyboardEvent.preventDefault();
    if (nativeVideo) {
      if (action === "toggle") {
        if (nativeVideo.paused) void nativeVideo.play();
        else nativeVideo.pause();
      } else {
        nativeVideo.currentTime = Math.max(0, nativeVideo.currentTime + (action === "rewind" ? -5 : 5));
      }
      return;
    }
    if (action === "toggle") {
      void toggleStreamPlayback(iframe as HTMLIFrameElement).catch(() => undefined);
    } else {
      void adjustStreamPlayback(iframe as HTMLIFrameElement, action === "rewind" ? -5_000 : 5_000).catch(() => undefined);
    }
  };
  window.addEventListener("keydown", playbackShortcutHandler);
  const seekPlayback = (startMs: number): void => {
    const pageUrl = new URL(window.location.href);
    pageUrl.searchParams.set("t", String(startMs));
    window.history.replaceState(null, "", pageUrl);
    const nativeVideo = document.querySelector<HTMLVideoElement>("#archive-instant-video");
    if (nativeVideo) {
      nativeVideo.currentTime = Math.max(0, startMs / 1_000);
      void nativeVideo.play().catch(() => undefined);
      nativeVideo.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const iframe = document.querySelector<HTMLIFrameElement>(".stream-player iframe");
    if (iframe) {
      const reloadAtMarker = (): void => {
        if (iframe.isConnected) iframe.src = playbackUrlAt(iframe.src, startMs, true);
      };
      if (!seekAndPlayStream(iframe, startMs, reloadAtMarker)) reloadAtMarker();
      iframe.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };
  const currentPlaybackPosition = async (): Promise<number> => {
    const nativeVideo = document.querySelector<HTMLVideoElement>("#archive-instant-video");
    if (nativeVideo) return Math.round(nativeVideo.currentTime * 1_000);
    const iframe = document.querySelector<HTMLIFrameElement>(".stream-player iframe");
    if (!iframe) throw new Error("Start video playback before capturing its current position.");
    return currentStreamPositionMs(iframe);
  };
  options.app.innerHTML = `
    <main class="shell library-shell">
      <header class="topbar page-title-topbar">
        ${options.brand}
        <div class="mobile-page-heading" aria-hidden="true"><h1 id="mobile-event-title">Event details.</h1></div>
        ${options.menu}
      </header>
      <section id="event-detail" class="event-detail"><p>Loading Event…</p></section>
    </main>`;
  wireThumbnailImageFallbacks(options.app);
  const detail = document.querySelector<HTMLElement>("#event-detail");
  try {
    const response = await fetch(`/api/events/${encodeURIComponent(eventId)}`, { headers: { Accept: "application/json" } });
    if (!response.ok) throw new Error(response.status === 404 ? "This Event is unavailable or outside your Service Type access." : "The Event could not be loaded.");
    const { event } = (await response.json()) as EventDetailResponse;
    const showIndexedMedia = Boolean(options.canViewIndexedMedia) && !["scheduled", "live"].includes(event.eventStatus);
    const mobileEventTitle = document.querySelector<HTMLElement>("#mobile-event-title");
    if (mobileEventTitle) mobileEventTitle.textContent = event.title;
    if (detail) detail.innerHTML = `
      <a class="text-link back-link" href="/">← Back to library</a>
      <h1 class="event-detail-title">${escapeHtml(event.title)}</h1>
      <p class="lede">${escapeHtml(formatTime(event.scheduledStartAt))}</p>
      <div class="event-playback-layout ${showIndexedMedia ? "has-indexed-media" : ""}">
        <div class="event-player-column">
          ${event.videoNotice ? `<aside class="event-video-notice" role="note"><strong>Video notice</strong><p>${escapeHtml(event.videoNotice.message)}</p><small>Reviewed ${escapeHtml(formatTime(event.videoNotice.reviewedAt))}</small></aside>` : ""}
          <div class="player-placeholder ${event.eventStatus}" id="event-player">
            <span>${escapeHtml(stateLabel(event))}</span>
            ${event.storageStatus === "archived"
              ? `<div class="archive-playback-actions">${options.canPlayVideos ? '<button class="primary-button compact" id="archive-instant-view" type="button">Google Archive Instant View</button>' : ""}</div><small id="restore-message">Instant View plays from the archive now.<br><br>Full Restore takes 15-20 minutes to complete,<br>but allows for transcription and markers to be reprocessed.</small>${options.canRestoreArchived ? '<button class="secondary-button compact" id="restore-video" type="button">Cloudflare Full Restore View</button>' : ""}`
              : event.storageStatus === "restore_pending"
                ? `<div class="archive-playback-actions">${options.canPlayVideos ? '<button class="primary-button compact" id="archive-instant-view" type="button">Google Archive Instant View</button>' : ""}${options.canRestoreArchived ? '<button class="secondary-button compact" id="restore-video" type="button" hidden>Try Cloudflare Full Restore Again</button>' : ""}</div><small id="restore-message">Cloudflare Full Restore View is in progress. Instant View is available while it finishes.</small>`
                : '<small>Video playback will be available after this Event is recorded and processed.</small>'}
            ${["archived", "restore_pending"].includes(event.storageStatus) ? `<div class="restore-progress-panel" id="restore-progress-panel" ${event.storageStatus === "archived" ? "hidden" : ""} aria-live="polite">
              <progress id="restore-progress" max="100"></progress>
              <strong id="restore-progress-heading">Preparing restore</strong>
              <small id="restore-progress-detail">Checking archive transfer status…</small>
            </div>` : ""}
          </div>
          ${event.pcoPlanId ? `<a class="pco-plan-link" id="pco-plan-link" href="/events/${encodeURIComponent(event.id)}/plan-summary">View Plan Summary</a>` : ""}
          ${showIndexedMedia ? '<div id="marker-editor-container" class="marker-editor-container"></div>' : ""}
        </div>
        ${showIndexedMedia ? '<div id="marker-container" class="event-marker-container"></div>' : ""}
      </div>
      ${options.canViewVideoComments ? `<section class="video-comments" aria-labelledby="video-comments-title">
        <h2 class="sr-only" id="video-comments-title">Video notes and comments</h2>
        ${options.canAddVideoComments ? `<form class="video-comment-form" id="video-comment-form"><label class="config-field"><span class="sr-only">Add a note or comment</span><textarea name="body" rows="2" maxlength="1000" required placeholder="Add a note or comment…"></textarea></label><button class="primary-button compact" type="submit">Post</button><label class="video-comment-private"><input name="isPrivate" type="checkbox"><span>PRIVATE (for me only)</span></label><p class="form-message" role="status" aria-live="polite"></p></form>` : ""}
        <div class="video-comment-list" id="video-comment-list"></div><button class="video-comment-more" id="video-comment-more" type="button" hidden>…more</button>
      </section>` : ""}
      ${showIndexedMedia ? '<div id="transcript-container" class="event-transcript-container"></div>' : ""}
      <details class="event-details-section" id="event-details">
        <summary><span><strong>Event Details</strong><small>Status, metadata, thumbnail, and Video controls.</small></span><span aria-hidden="true">+</span></summary>
        <div class="event-details-content" id="event-details-content">
          <details class="event-status-disclosure">
            <summary><span><strong>Event Status</strong><small>Media, storage, capture, and retention state.</small></span><span aria-hidden="true">+</span></summary>
            <div class="event-status-body">
              <dl class="event-metadata">
                <div><dt>Event status</dt><dd>${escapeHtml(event.eventStatus)}</dd></div>
                <div><dt>Media status</dt><dd>${escapeHtml(event.mediaStatus)}</dd></div>
                <div><dt>Storage status</dt><dd>${escapeHtml(event.storageStatus)}</dd></div>
                <div><dt>Video source</dt><dd>${event.ingestMethod === "file_upload" ? "Uploaded file" : "Live capture"}</dd></div>
                <div><dt>Planning Center Plan</dt><dd>${event.pcoPlanLabel ? escapeHtml(event.pcoPlanLabel) : "Not associated"} <small>(${event.pcoPlanAssociationMode === "manual" ? "manual" : "automatic"})</small></dd></div>
                <div><dt>Manual tags</dt><dd>${event.manualTags.length ? event.manualTags.map((tag) => `<span class="event-tag-chip">${escapeHtml(tag.value)}</span>`).join(" ") : "None"}</dd></div>
                <div><dt>Capture window</dt><dd>${escapeHtml(formatTime(event.captureStartAt))} – ${escapeHtml(formatTime(event.captureEndAt))}</dd></div>
                <div><dt>Online expiration</dt><dd>${event.keepOnline ? "Kept online" : event.onlineExpiresAt ? escapeHtml(formatTime(event.onlineExpiresAt)) : "Not scheduled"}</dd></div>
                <div><dt>Archive protection</dt><dd>${event.protectFromDeletion ? "Protected" : "Standard policy"}</dd></div>
              </dl>
            </div>
          </details>
        </div>
      </details>
      ${options.canShareVideo ? `<details class="event-details-section"><summary><span><strong>Custom Share Link</strong><small>Create a 30-day link that does not require a Planning Center login.</small></span><span aria-hidden="true">+</span></summary><div class="event-details-content"><form id="video-invitation-form"><label class="config-field"><span>Recipient name</span><input name="recipientName" required maxlength="200" autocomplete="name"></label><label class="config-field"><span>Recipient email</span><input name="recipientEmail" type="email" required maxlength="320" autocomplete="email"></label><p class="share-link-warning">Anyone who receives this link can watch the Video until it expires or a Manager revokes it.</p><div class="form-message" role="status" aria-live="polite"></div><button class="secondary-button compact" type="submit">Create Custom Link</button></form><div class="custom-share-links" id="custom-share-links"><p class="form-message">Loading Custom Share Links…</p></div></div></details>` : ""}
      ${options.canViewAnalytics ? '<details class="event-details-section viewing-analytics"><summary><span><strong>Viewing analytics</strong><small>Live and recorded viewing for this Video.</small></span><span aria-hidden="true">+</span></summary><div class="event-details-content" id="video-viewing-analytics"><p class="form-message">Loading viewing analytics…</p></div></details>' : ""}`;
    const eventDetailsContent = document.querySelector<HTMLElement>("#event-details-content");
    const commentList = document.querySelector<HTMLElement>("#video-comment-list");
    const analyticsPanel = document.querySelector<HTMLElement>("#video-viewing-analytics");
    if (analyticsPanel) void (async () => {
      try {
        const response = await fetch(`/api/videos/${encodeURIComponent(event.videoId)}/viewing-analytics`, { headers: { Accept: "application/json" } });
        if (!response.ok) throw new Error("Viewing analytics could not be loaded.");
        const analytics = await response.json() as { live: { uniqueViewers: number; views: number; minutesWatched: number }; recorded: { uniqueViewers: number; views: number; minutesWatched: number }; viewers: Array<{ name: string; email: string; liveMinutes: number; recordedMinutes: number }> };
        const stat = (label: string, value: number) => `<div><strong>${value}</strong><span>${label}</span></div>`;
        analyticsPanel.innerHTML = `<div class="viewing-analytics-summary"><section><h3>Live</h3>${stat("unique viewers", analytics.live.uniqueViewers)}${stat("views", analytics.live.views)}${stat("minutes watched", analytics.live.minutesWatched)}</section><section><h3>Recorded</h3>${stat("unique viewers", analytics.recorded.uniqueViewers)}${stat("views", analytics.recorded.views)}${stat("minutes watched", analytics.recorded.minutesWatched)}</section></div>${analytics.viewers.length ? `<div class="viewing-analytics-viewers">${analytics.viewers.map((viewer) => `<p><strong>${escapeHtml(viewer.name)}</strong><span>${viewer.liveMinutes} live min · ${viewer.recordedMinutes} recorded min</span></p>`).join("")}</div>` : '<p class="form-message">No viewing activity has been recorded yet.</p>'}`;
      } catch (error) { analyticsPanel.innerHTML = `<p class="error-message">${escapeHtml(error instanceof Error ? error.message : "Viewing analytics could not be loaded.")}</p>`; }
    })();
    const commentMore = document.querySelector<HTMLButtonElement>("#video-comment-more");
    let commentsExpanded = false;
    let loadedComments: VideoCommentsResponse["comments"] = [];
    const updateCommentOverflow = (): void => {
      if (!commentList || !commentMore) return;
      commentList.classList.toggle("is-collapsed", !commentsExpanded);
      const hasHiddenLines = !commentsExpanded && commentList.scrollHeight > commentList.clientHeight + 1;
      commentList.classList.toggle("is-collapsed", hasHiddenLines);
      commentMore.hidden = !hasHiddenLines;
    };
    const renderComments = (comments: VideoCommentsResponse["comments"]): void => {
      if (!commentList) return;
      loadedComments = comments;
      commentList.innerHTML = comments.length
        ? comments.map((comment) => `<article class="video-comment"><strong>${escapeHtml(comment.authorName)}</strong><p title="${escapeHtml(comment.body)}">${escapeHtml(comment.body).replace(/\n/g, " ")}</p><span>${comment.isPrivate ? '<small class="video-comment-private-badge">Private</small>' : ""}<time datetime="${escapeHtml(comment.createdAt)}">${escapeHtml(formatTime(comment.createdAt))}</time>${comment.canDelete ? `<button type="button" class="video-comment-delete" data-comment-id="${escapeHtml(comment.id)}" aria-label="Delete this comment" title="Delete comment">🗑</button>` : ""}</span></article>`).join("")
        : "";
      updateCommentOverflow();
    };
    const loadComments = async (): Promise<void> => {
      try {
        const commentsResponse = await fetch(`/api/videos/${encodeURIComponent(event.videoId)}/comments`, { headers: { Accept: "application/json" } });
        if (!commentsResponse.ok) throw new Error("Video comments could not be loaded.");
        renderComments((await commentsResponse.json() as VideoCommentsResponse).comments);
      } catch (error) {
        if (commentList) commentList.innerHTML = `<p class="error-message">${escapeHtml(error instanceof Error ? error.message : "Video comments could not be loaded.")}</p>`;
      }
    };
    void loadComments();
    const commentForm = document.querySelector<HTMLFormElement>("#video-comment-form");
    const commentInput = commentForm?.querySelector<HTMLTextAreaElement>('textarea[name="body"]');
    commentInput?.addEventListener("keydown", (keyEvent) => {
      if (keyEvent.key !== "Enter" || keyEvent.shiftKey || keyEvent.isComposing) return;
      keyEvent.preventDefault();
      commentForm?.requestSubmit();
    });
    commentForm?.addEventListener("submit", (submitEvent) => {
      submitEvent.preventDefault(); void (async () => {
        const message = commentForm.querySelector<HTMLElement>(".form-message");
        const button = commentForm.querySelector<HTMLButtonElement>('button[type="submit"]');
          const formData = new FormData(commentForm);
          const body = String(formData.get("body") ?? "").trim();
          const isPrivate = formData.get("isPrivate") === "on";
        if (!body) { if (message) message.textContent = "Enter a note or comment."; return; }
        try {
          if (button) button.disabled = true;
          if (message) message.textContent = "Posting…";
          const postResponse = await fetch(`/api/videos/${encodeURIComponent(event.videoId)}/comments`, {
            method: "POST", headers: { Accept: "application/json", "Content-Type": "application/json", Origin: window.location.origin }, body: JSON.stringify({ body, isPrivate }),
          });
          if (!postResponse.ok) throw new Error(await responseErrorMessage(postResponse));
          commentForm.reset();
          if (message) message.textContent = "Posted.";
          await loadComments();
        } catch (error) {
          if (message) { message.textContent = error instanceof Error ? error.message : "The comment could not be posted."; message.className = "form-message error-message"; }
        } finally { if (button) button.disabled = false; }
      })();
    });
    commentMore?.addEventListener("click", () => { commentsExpanded = true; renderComments(loadedComments); });
    commentList?.addEventListener("click", (clickEvent) => {
      const target = clickEvent.target;
      const button = target instanceof Element ? target.closest<HTMLButtonElement>(".video-comment-delete") : null;
      if (!button || !window.confirm("Delete this comment?")) return;
      void (async () => {
        button.disabled = true;
        try {
          const deleteResponse = await fetch(`/api/videos/${encodeURIComponent(event.videoId)}/comments/${encodeURIComponent(button.dataset.commentId ?? "")}`, {
            method: "DELETE", headers: { Accept: "application/json", Origin: window.location.origin },
          });
          if (!deleteResponse.ok) throw new Error(await responseErrorMessage(deleteResponse));
          await loadComments();
        } catch (error) {
          button.disabled = false;
          window.alert(error instanceof Error ? error.message : "The comment could not be deleted.");
        }
      })();
    });
    if (detail && options.canManageOneTimeEvents && ["ended", "failed"].includes(event.eventStatus)
      && ["waiting", "failed"].includes(event.mediaStatus) && event.storageStatus === "none") {
      detail.insertAdjacentHTML("beforeend", `<details class="event-editor" open>
        <summary><span><strong>Upload Event video</strong><small>Attach a completed recording when live capture was unavailable or failed.</small></span><span aria-hidden="true">+</span></summary>
        <form id="event-upload-form">
          <label class="config-field"><span>Video file</span><input name="video" type="file" accept="video/*,.mkv,.mxf,.gxf,.lxf,.ts" required></label>
          <progress class="video-upload-progress" value="0" max="100" hidden></progress>
          <p class="form-message" role="status" aria-live="polite">The video uploads directly to Cloudflare Stream and will then enter the normal archive workflow.</p>
          <button class="primary-button compact" type="submit">Upload Video</button>
        </form>
      </details>`);
      const uploadForm = document.querySelector<HTMLFormElement>("#event-upload-form");
      uploadForm?.addEventListener("submit", (submitEvent) => {
        submitEvent.preventDefault(); void (async () => {
          const fileInput = uploadForm.elements.namedItem("video") as HTMLInputElement;
          const file = fileInput.files?.[0];
          const message = uploadForm.querySelector<HTMLElement>(".form-message");
          const progress = uploadForm.querySelector<HTMLProgressElement>("progress");
          const button = uploadForm.querySelector<HTMLButtonElement>('button[type="submit"]');
          if (!file) { if (message) message.textContent = "Choose a video file."; return; }
          try {
            if (button) { button.disabled = true; button.textContent = "Uploading…"; }
            await provisionAndUpload(event.id, file, message, progress);
            window.location.reload();
          } catch (error) {
            if (button) { button.disabled = false; button.textContent = "Upload Video"; }
            if (message) { message.textContent = error instanceof Error ? error.message : "The video could not be uploaded."; message.className = "form-message error-message"; }
          }
        })();
      });
    }
    if (detail && options.canManageOneTimeEvents && event.ingestMethod === "file_upload" && event.mediaStatus === "processing") {
      detail.insertAdjacentHTML("beforeend", `<section class="event-upload-processing" aria-live="polite"><p class="eyebrow">Uploaded video</p><h2>Cloudflare is processing this video.</h2><p id="event-upload-status">Checking processing status…</p></section>`);
      const uploadStatus = document.querySelector<HTMLElement>("#event-upload-status");
      let checks = 0;
      const checkUpload = async (): Promise<void> => {
        checks += 1;
        try {
          const statusResponse = await fetch(`/api/events/${encodeURIComponent(event.id)}/upload/status`, {
            method: "POST",
            headers: { Accept: "application/json", "Content-Type": "application/json", Origin: window.location.origin },
            body: "{}",
          });
          if (!statusResponse.ok) throw new Error(await responseErrorMessage(statusResponse));
          const status = await statusResponse.json() as EventUploadStatusResponse;
          if (status.status === "ready") { window.location.reload(); return; }
          if (status.status === "failed") { if (uploadStatus) uploadStatus.textContent = "Cloudflare could not process the uploaded video. You may start a new upload."; return; }
          if (uploadStatus) uploadStatus.textContent = status.status === "uploading" ? "The browser upload has not finished yet." : "Cloudflare is encoding the video for playback.";
          if (checks < 120) window.setTimeout(() => void checkUpload(), 5_000);
        } catch (error) {
          if (uploadStatus) uploadStatus.textContent = error instanceof Error ? error.message : "Upload status is temporarily unavailable.";
        }
      };
      void checkUpload();
    }
    if (eventDetailsContent && options.canEditEventMetadata) {
      const manualTagsMarkup = event.manualTags.length
        ? event.manualTags.map((tag) => `<span class="event-tag-editor-chip">${escapeHtml(tag.value)}<button type="button" data-event-tag-id="${escapeHtml(tag.id)}" aria-label="Remove ${escapeHtml(tag.value)}">×</button></span>`).join("")
        : '<span class="event-tag-empty">No manual tags yet.</span>';
      const thumbnailModeLabel = event.thumbnailMode === "custom"
        ? "Custom upload"
        : event.thumbnailMode === "abbreviation" ? "Default abbreviation" : "Schedule or Planning Center artwork";
      eventDetailsContent.insertAdjacentHTML("beforeend", `
        <details class="event-detail-subsection">
          <summary><span><strong>Edit Event Details</strong><small>Change the display title and notes.</small></span><span aria-hidden="true">+</span></summary>
          <form id="event-metadata-form"><label class="config-field"><span>Title</span><input name="title" required maxlength="200" value="${escapeHtml(event.title)}"></label><label class="config-field"><span>Notes</span><textarea name="notes" rows="3" maxlength="2000">${escapeHtml(event.notes ?? "")}</textarea></label><p class="form-message" role="status" aria-live="polite"></p><button class="primary-button compact" type="submit">Save Event Details</button></form>
        </details>
        <details class="event-detail-subsection">
          <summary><span><strong>Manual Event Tags</strong><small>Add DVR-only labels without changing Planning Center data.</small></span><span aria-hidden="true">+</span></summary>
          <div class="event-tag-editor-list">${manualTagsMarkup}</div>
          <form id="event-tag-form"><label class="config-field"><span>New tag</span><input name="value" required maxlength="50" placeholder="Christmas Eve"></label><div class="event-tag-suggestions" hidden aria-live="polite"></div><p class="form-message" role="status" aria-live="polite"></p><button class="primary-button compact" type="submit">Add Tag</button></form>
        </details>
        <details class="event-detail-subsection">
          <summary><span><strong>Event Thumbnail</strong><small>Use Planning Center artwork, upload a custom image, or show the default abbreviation.</small></span><span aria-hidden="true">+</span></summary>
          <form id="event-thumbnail-form">
            <div class="event-thumbnail event-thumbnail-editor-preview" aria-hidden="true">
              <span>${escapeHtml(event.serviceTypeName.slice(0, 2).toUpperCase())}</span>
              ${event.thumbnailUrl ? `<img src="${escapeHtml(event.thumbnailUrl)}" alt="" referrerpolicy="no-referrer">` : ""}
            </div>
            <p class="thumbnail-current-source">Current thumbnail: <strong>${escapeHtml(thumbnailModeLabel)}</strong></p>
            <label class="config-field"><span>Custom thumbnail</span><input name="thumbnail" type="file" accept="image/jpeg,image/png,image/webp"><small>JPEG, PNG, or WebP. Maximum 1.5 MB. Landscape images work best.</small></label>
            <p class="form-message" role="status" aria-live="polite"></p>
            <div class="thumbnail-actions">
              <button class="primary-button compact" type="submit">Upload Custom Thumbnail</button>
              <button class="secondary-button compact" type="button" data-thumbnail-mode="pco">Use Schedule / Planning Center Artwork</button>
              <button class="secondary-button compact" type="button" data-thumbnail-mode="abbreviation">Use Default Abbreviation</button>
            </div>
          </form>
        </details>`);
      const editForm = document.querySelector<HTMLFormElement>("#event-metadata-form");
      editForm?.addEventListener("submit", (submitEvent) => { submitEvent.preventDefault(); void (async () => {
        const data = new FormData(editForm); const message = editForm.querySelector<HTMLElement>(".form-message");
        const response = await fetch(`/api/events/${encodeURIComponent(event.id)}`, { method: "PATCH", headers: { Accept: "application/json", "Content-Type": "application/json", Origin: window.location.origin }, body: JSON.stringify({ title: String(data.get("title")), notes: String(data.get("notes")).trim() || null }) });
        if (response.ok) window.location.reload(); else if (message) message.textContent = "Event Details could not be saved.";
      })(); });
      const tagForm = document.querySelector<HTMLFormElement>("#event-tag-form");
      const addTag = async (value: string, button?: HTMLButtonElement): Promise<void> => {
        const message = tagForm?.querySelector<HTMLElement>(".form-message");
        try {
          if (button) button.disabled = true;
          if (message) message.textContent = "Adding tag…";
          const tagResponse = await fetch(`/api/events/${encodeURIComponent(event.id)}/tags`, {
            method: "POST",
            headers: { Accept: "application/json", "Content-Type": "application/json", Origin: window.location.origin },
            body: JSON.stringify({ value }),
          });
          if (!tagResponse.ok) throw new Error(await responseErrorMessage(tagResponse));
          window.location.reload();
        } catch (error) {
          if (button) button.disabled = false;
          if (message) message.textContent = error instanceof Error ? error.message : "The tag could not be added.";
        }
      };
      tagForm?.addEventListener("submit", (submitEvent) => {
        submitEvent.preventDefault(); void (async () => {
          const data = new FormData(tagForm);
          const button = tagForm.querySelector<HTMLButtonElement>('button[type="submit"]');
          await addTag(String(data.get("value")), button ?? undefined);
        })();
      });
      const suggestions = tagForm?.querySelector<HTMLElement>(".event-tag-suggestions");
      if (suggestions) {
        void (async () => {
          try {
            const response = await fetch(`/api/events/${encodeURIComponent(event.id)}/tags`, { headers: { Accept: "application/json" } });
            if (!response.ok) return;
            const payload = await response.json() as { tags?: unknown };
            const existing = new Set(event.manualTags.map((tag) => tag.value.toLocaleLowerCase("en-US")));
            const available = Array.isArray(payload.tags)
              ? payload.tags.filter((tag): tag is string => typeof tag === "string" && !existing.has(tag.toLocaleLowerCase("en-US")))
              : [];
            if (!available.length) return;
            suggestions.hidden = false;
            suggestions.innerHTML = `<span>Available tags</span><div>${available.map((tag) => `<button type="button" class="event-tag-suggestion" data-event-tag-value="${escapeHtml(tag)}" aria-label="Add ${escapeHtml(tag)}">${escapeHtml(tag)} <b aria-hidden="true">+</b></button>`).join("")}</div>`;
            suggestions.querySelectorAll<HTMLButtonElement>("[data-event-tag-value]").forEach((button) => {
              button.addEventListener("click", () => void addTag(button.dataset.eventTagValue ?? "", button));
            });
          } catch { /* Suggestions are optional; custom tag entry remains available. */ }
        })();
      }
      document.querySelectorAll<HTMLButtonElement>("[data-event-tag-id]").forEach((button) => {
        button.addEventListener("click", () => { void (async () => {
          const tagId = button.dataset.eventTagId;
          if (!tagId) return;
          try {
            button.disabled = true;
            const removeResponse = await fetch(`/api/events/${encodeURIComponent(event.id)}/tags/${encodeURIComponent(tagId)}`, {
              method: "DELETE",
              headers: { Accept: "application/json", Origin: window.location.origin },
            });
            if (!removeResponse.ok) throw new Error(await responseErrorMessage(removeResponse));
            window.location.reload();
          } catch (error) {
            button.disabled = false;
            const message = tagForm?.querySelector<HTMLElement>(".form-message");
            if (message) message.textContent = error instanceof Error ? error.message : "The tag could not be removed.";
          }
        })(); });
      });
      const thumbnailForm = document.querySelector<HTMLFormElement>("#event-thumbnail-form");
      const thumbnailMessage = thumbnailForm?.querySelector<HTMLElement>(".form-message");
      thumbnailForm?.addEventListener("submit", (submitEvent) => {
        submitEvent.preventDefault(); void (async () => {
          const input = thumbnailForm.elements.namedItem("thumbnail") as HTMLInputElement;
          const file = input.files?.[0];
          const button = thumbnailForm.querySelector<HTMLButtonElement>('button[type="submit"]');
          if (!file) { if (thumbnailMessage) thumbnailMessage.textContent = "Choose a thumbnail image."; return; }
          if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
            if (thumbnailMessage) thumbnailMessage.textContent = "Choose a JPEG, PNG, or WebP image.";
            return;
          }
          if (file.size > 1_500_000) {
            if (thumbnailMessage) thumbnailMessage.textContent = "The thumbnail must be 1.5 MB or smaller.";
            return;
          }
          try {
            if (button) button.disabled = true;
            if (thumbnailMessage) thumbnailMessage.textContent = "Uploading thumbnail…";
            const uploadResponse = await fetch(`/api/events/${encodeURIComponent(event.id)}/thumbnail`, {
              method: "PUT",
              headers: { Accept: "application/json", "Content-Type": file.type, Origin: window.location.origin },
              body: file,
            });
            if (!uploadResponse.ok) throw new Error(await responseErrorMessage(uploadResponse));
            window.location.reload();
          } catch (error) {
            if (button) button.disabled = false;
            if (thumbnailMessage) thumbnailMessage.textContent = error instanceof Error ? error.message : "The thumbnail could not be uploaded.";
          }
        })();
      });
      thumbnailForm?.querySelectorAll<HTMLButtonElement>("[data-thumbnail-mode]").forEach((button) => {
        button.addEventListener("click", () => { void (async () => {
          const mode = button.dataset.thumbnailMode;
          if (!(mode === "pco" || mode === "abbreviation")) return;
          const label = mode === "pco" ? "Planning Center artwork" : "the default abbreviation";
          if (!window.confirm(`Use ${label} for this Event? Any custom thumbnail will be removed.`)) return;
          try {
            button.disabled = true;
            if (thumbnailMessage) thumbnailMessage.textContent = "Changing thumbnail…";
            const modeResponse = await fetch(`/api/events/${encodeURIComponent(event.id)}/thumbnail`, {
              method: "PATCH",
              headers: { Accept: "application/json", "Content-Type": "application/json", Origin: window.location.origin },
              body: JSON.stringify({ mode, confirmation: "CHANGE" }),
            });
            if (!modeResponse.ok) throw new Error(await responseErrorMessage(modeResponse));
            window.location.reload();
          } catch (error) {
            button.disabled = false;
            if (thumbnailMessage) thumbnailMessage.textContent = error instanceof Error ? error.message : "The thumbnail could not be changed.";
          }
        })(); });
      });
    }
    if (eventDetailsContent && options.canManagePlanAssociation) {
      const planOptions = event.pcoPlanOptions.map((plan) => `<option value="manual:${escapeHtml(plan.id)}" ${event.pcoPlanAssociationMode === "manual" && event.pcoPlanId === plan.id ? "selected" : ""}>${escapeHtml(plan.label)}</option>`).join("");
      eventDetailsContent.insertAdjacentHTML("beforeend", `
        <details class="event-detail-subsection">
          <summary><span><strong>Planning Center Plan</strong><small>Correct the Plan association or return it to automatic date matching.</small></span><span aria-hidden="true">+</span></summary>
          <form id="event-plan-association-form">
            <label class="config-field"><span>Association</span><select name="association">
              <option value="automatic" ${event.pcoPlanAssociationMode === "automatic" ? "selected" : ""}>Automatic Service Type + date matching</option>
              <option value="manual:" ${event.pcoPlanAssociationMode === "manual" && !event.pcoPlanId ? "selected" : ""}>No Planning Center Plan (manual)</option>
              ${planOptions}
            </select><small>Changing Plans clears only unconfirmed Planning Center proposals. Confirmed Markers stay fixed.</small></label>
            <p class="form-message" role="status" aria-live="polite"></p>
            <button class="primary-button compact" type="submit">Save Plan Association</button>
          </form>
        </details>`);
      const planForm = document.querySelector<HTMLFormElement>("#event-plan-association-form");
      planForm?.addEventListener("submit", (submitEvent) => {
        submitEvent.preventDefault(); void (async () => {
          const data = new FormData(planForm);
          const association = String(data.get("association"));
          const isAutomatic = association === "automatic";
          const button = planForm.querySelector<HTMLButtonElement>('button[type="submit"]');
          const message = planForm.querySelector<HTMLElement>(".form-message");
          try {
            if (button) button.disabled = true;
            if (message) message.textContent = "Saving association…";
            const planResponse = await fetch(`/api/events/${encodeURIComponent(event.id)}/plan-association`, {
              method: "PATCH",
              headers: { Accept: "application/json", "Content-Type": "application/json", Origin: window.location.origin },
              body: JSON.stringify({
                mode: isAutomatic ? "automatic" : "manual",
                pcoPlanId: isAutomatic ? null : association.slice("manual:".length) || null,
              }),
            });
            if (!planResponse.ok) throw new Error(await responseErrorMessage(planResponse));
            window.location.reload();
          } catch (error) {
            if (button) button.disabled = false;
            if (message) message.textContent = error instanceof Error ? error.message : "The Plan association could not be saved.";
          }
        })();
      });
    }
    if (detail && options.canAdjustCaptureWindows && event.eventStatus === "scheduled") {
      const preRoll = Math.round((new Date(event.scheduledStartAt).valueOf() - new Date(event.captureStartAt).valueOf()) / 60_000);
      const postRoll = Math.round((new Date(event.captureEndAt).valueOf() - new Date(event.scheduledEndAt).valueOf()) / 60_000);
      detail.insertAdjacentHTML("beforeend", `<details class="event-editor"><summary><span><strong>Adjust capture window</strong><small>Times use America/New_York.</small></span><span aria-hidden="true">+</span></summary>
        <form id="event-window-form"><div class="config-grid two-column"><label class="config-field"><span>Event date</span><input name="eventDateLocal" type="date" required value="${escapeHtml(event.eventDateLocal)}"></label><label class="config-field"><span>Start time</span><input name="scheduledStartTimeLocal" type="time" required value="${escapeHtml(localTime(event.scheduledStartAt))}"></label><label class="config-field"><span>End time</span><input name="scheduledEndTimeLocal" type="time" required value="${escapeHtml(localTime(event.scheduledEndAt))}"></label><label class="config-field"><span>Pre-roll minutes</span><input name="preRollMinutes" type="number" min="0" max="180" required value="${preRoll}"></label><label class="config-field"><span>Post-roll minutes</span><input name="postRollMinutes" type="number" min="0" max="180" required value="${postRoll}"></label></div><p class="form-message" role="status" aria-live="polite"></p><button class="primary-button compact" type="submit">Save capture window</button></form></details>`);
      const windowForm = document.querySelector<HTMLFormElement>("#event-window-form");
      windowForm?.addEventListener("submit", (submitEvent) => { submitEvent.preventDefault(); void (async () => {
        const data = new FormData(windowForm); const message = windowForm.querySelector<HTMLElement>(".form-message");
        const body = { eventDateLocal: String(data.get("eventDateLocal")), scheduledStartTimeLocal: String(data.get("scheduledStartTimeLocal")), scheduledEndTimeLocal: String(data.get("scheduledEndTimeLocal")), preRollMinutes: Number(data.get("preRollMinutes")), postRollMinutes: Number(data.get("postRollMinutes")) };
        const response = await fetch(`/api/events/${encodeURIComponent(event.id)}/capture-window`, { method: "PATCH", headers: { Accept: "application/json", "Content-Type": "application/json", Origin: window.location.origin }, body: JSON.stringify(body) });
        if (response.ok) window.location.reload(); else if (message) message.textContent = "The capture window could not be saved.";
      })(); });
    }
    if (eventDetailsContent && (options.canManageVisibility || options.canManageRetention)) {
      eventDetailsContent.insertAdjacentHTML("beforeend", `
        <details class="event-detail-subsection video-controls">
          <summary><span><strong>Video Controls</strong><small>Manage retention and Library visibility.</small></span><span aria-hidden="true">+</span></summary>
          <form id="video-controls-form">
            <label class="config-toggle"><input type="checkbox" name="keepOnline" ${event.keepOnline ? "checked" : ""} ${options.canManageRetention ? "" : "disabled"}><span><strong>Keep Online</strong><small>Prevent normal Cloudflare online-retention cleanup.</small></span></label>
            <label class="config-toggle"><input type="checkbox" name="protectFromDeletion" ${event.protectFromDeletion ? "checked" : ""} ${options.canManageRetention ? "" : "disabled"}><span><strong>Protect from Deletion</strong><small>Preserve the archive if a future archive-deletion policy is enabled.</small></span></label>
            <label class="config-toggle"><input type="checkbox" name="isHidden" ${event.isHidden ? "checked" : ""} ${options.canManageVisibility ? "" : "disabled"}><span><strong>Hide from Library</strong><small>Managers can still find hidden Videos by enabling Include hidden Videos.</small></span></label>
            <p class="form-message" role="status" aria-live="polite"></p>
            <button class="primary-button compact" type="submit">Save Video Controls</button>
          </form>
        </details>`);
      const controlsForm = document.querySelector<HTMLFormElement>("#video-controls-form");
      controlsForm?.addEventListener("submit", (submitEvent) => {
        submitEvent.preventDefault();
        void (async () => {
          const button = controlsForm.querySelector<HTMLButtonElement>('button[type="submit"]');
          const message = controlsForm.querySelector<HTMLElement>(".form-message");
          const checked = (name: string, fallback: boolean): boolean => {
            const input = controlsForm.elements.namedItem(name) as HTMLInputElement;
            return input.disabled ? fallback : input.checked;
          };
          const body: UpdateVideoControlsRequest = {
            keepOnline: checked("keepOnline", event.keepOnline),
            protectFromDeletion: checked("protectFromDeletion", event.protectFromDeletion),
            isHidden: checked("isHidden", event.isHidden),
          };
          try {
            if (button) button.disabled = true;
            if (message) message.textContent = "Saving…";
            const controlsResponse = await fetch(`/api/videos/${encodeURIComponent(event.videoId)}/controls`, {
              method: "PATCH",
              headers: { Accept: "application/json", "Content-Type": "application/json", Origin: window.location.origin },
              body: JSON.stringify(body),
            });
            if (!controlsResponse.ok) throw new Error(await responseErrorMessage(controlsResponse));
            window.location.reload();
          } catch (error) {
            if (button) button.disabled = false;
            if (message) message.textContent = error instanceof Error ? error.message : "Video controls could not be saved.";
          }
        })();
      });
    }
    if (eventDetailsContent && options.canDeleteEvents) {
      eventDetailsContent.insertAdjacentHTML("beforeend", `
        <details class="event-detail-subsection event-danger-zone">
          <summary><span><strong>Delete Event</strong><small>Permanently remove this Event's individual media and hide its tombstone from the Library.</small></span><span aria-hidden="true">+</span></summary>
          <form id="event-delete-form">
            <p class="delete-warning">This action cannot be undone. Cloudflare Event clips are verified and deleted before DVR marks the Event deleted. Shared Source Recordings are preserved. If a required provider is unavailable, deletion stops safely for retry.</p>
            <label class="config-field"><span>Reason for deletion</span><textarea name="reason" rows="3" minlength="5" maxlength="500" required placeholder="Explain why this Event must be permanently deleted."></textarea></label>
            <label class="config-field"><span>Type "<span class="delete-confirmation-token">DELETE</span>" to confirm</span><input name="confirmation" required pattern="DELETE" autocomplete="off" spellcheck="false"></label>
            <p class="form-message" role="status" aria-live="polite"></p>
            <button class="danger-button" type="submit">Delete Event Permanently</button>
          </form>
        </details>`);
      const deleteForm = document.querySelector<HTMLFormElement>("#event-delete-form");
      deleteForm?.addEventListener("submit", (submitEvent) => {
        submitEvent.preventDefault();
        void (async () => {
          const data = new FormData(deleteForm);
          const button = deleteForm.querySelector<HTMLButtonElement>('button[type="submit"]');
          const message = deleteForm.querySelector<HTMLElement>(".form-message");
          try {
            if (button) { button.disabled = true; button.textContent = "Deleting…"; }
            if (message) { message.textContent = "Verifying and deleting Event media…"; message.className = "form-message"; }
            const deleteResponse = await fetch(`/api/admin/events/${encodeURIComponent(event.id)}`, {
              method: "DELETE",
              headers: { Accept: "application/json", "Content-Type": "application/json", Origin: window.location.origin },
              body: JSON.stringify({
                reason: String(data.get("reason")).trim(),
                confirmation: String(data.get("confirmation")),
              }),
            });
            if (!deleteResponse.ok) throw new Error(await responseErrorMessage(deleteResponse));
            window.location.assign("/");
          } catch (error) {
            if (button) { button.disabled = false; button.textContent = "Delete Event Permanently"; }
            if (message) {
              message.textContent = error instanceof Error ? error.message : "The Event could not be deleted safely.";
              message.className = "form-message error-message";
            }
          }
        })();
      });
    }
    if (event.eventStatus === "live") {
      const player = document.querySelector<HTMLElement>("#event-player");
      try {
        const liveResponse = await fetch(`/api/events/${encodeURIComponent(event.id)}/live`, { headers: { Accept: "application/json" } });
        if (!liveResponse.ok) throw new Error("Live playback is temporarily unavailable.");
        const playback = await liveResponse.json() as LiveEventPlaybackResponse;
        if (player) { player.outerHTML = `<div class="stream-player live-stream-player"><iframe src="${escapeHtml(playback.playerUrl)}" title="${escapeHtml(event.title)} live player" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe></div>`; const iframe = document.querySelector<HTMLIFrameElement>(".live-stream-player iframe"); if (iframe) wireViewingAnalytics(iframe, event.videoId, "live"); }
      } catch (error) { const message = player?.querySelector<HTMLElement>("small"); if (message) message.textContent = error instanceof Error ? error.message : "Live playback is temporarily unavailable."; }
    } else if (event.mediaStatus === "ready" && isCloudflarePlaybackStorageStatus(event.storageStatus)) {
      const player = document.querySelector<HTMLElement>("#event-player");
      try {
        const playbackResponse = await fetch(`/api/videos/${encodeURIComponent(event.videoId)}/playback`, {
          headers: { Accept: "application/json" },
        });
        if (!playbackResponse.ok) throw new Error("Playback is temporarily unavailable.");
        const playback = await playbackResponse.json() as VideoPlaybackResponse;
        if (player) {
          player.outerHTML = `<div class="stream-player"><iframe src="${escapeHtml(playbackUrlAt(playback.playerUrl, requestedStartMs))}" title="${escapeHtml(event.title)} video player" allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe></div>`;
          const streamPlayer = document.querySelector<HTMLElement>(".stream-player");
          const iframe = streamPlayer?.querySelector<HTMLIFrameElement>("iframe");
          if (streamPlayer && iframe) {
            wirePlaybackGestures(streamPlayer, iframe);
            wireViewingAnalytics(iframe, event.videoId, "recorded");
            void prepareStreamPlayer(iframe).catch(() => undefined);
          }
        }
      } catch (error) {
        const message = player?.querySelector<HTMLElement>("small");
        if (message) message.textContent = error instanceof Error ? error.message : "Playback is temporarily unavailable.";
      }
    }
    const customShareLinks = document.querySelector<HTMLElement>("#custom-share-links");
    const loadCustomShareLinks = async (): Promise<void> => {
      if (!customShareLinks) return;
      try {
        const response = await fetch(`/api/videos/${encodeURIComponent(event.videoId)}/invitations`, { headers: { Accept: "application/json" } });
        if (!response.ok) throw new Error("Custom Share Links could not be loaded.");
        const payload = await response.json() as { invitations: Array<{ id: string; recipientName: string; recipientEmail: string; createdAt: string; expiresAt: string; revokedAt: string | null }> };
        customShareLinks.innerHTML = payload.invitations.length ? `<h3>Custom Share Links</h3>${payload.invitations.map((invitation) => `<article class="custom-share-link-row"><div><strong>${escapeHtml(invitation.recipientName)}</strong><small>${escapeHtml(invitation.recipientEmail)} · ${invitation.revokedAt ? `Revoked ${escapeHtml(formatTime(invitation.revokedAt))}` : `Expires ${escapeHtml(formatTime(invitation.expiresAt))}`}</small></div>${invitation.revokedAt ? "" : `<button class="secondary-button compact revoke-custom-share-link" type="button" data-invitation-id="${escapeHtml(invitation.id)}">Revoke</button>`}</article>`).join("")}` : "<p class=\"form-message\">No Custom Share Links have been created.</p>";
        customShareLinks.querySelectorAll<HTMLButtonElement>(".revoke-custom-share-link").forEach((button) => button.addEventListener("click", () => {
          if (!window.confirm("Revoke this Custom Share Link immediately? Anyone using it will no longer be able to watch this Video.")) return;
          void (async () => {
            button.disabled = true;
            const response = await fetch(`/api/videos/${encodeURIComponent(event.videoId)}/invitations/${encodeURIComponent(button.dataset.invitationId ?? "")}`, { method: "DELETE", headers: { Accept: "application/json", Origin: window.location.origin } });
            if (response.ok) await loadCustomShareLinks(); else { button.disabled = false; window.alert("The Custom Share Link could not be revoked."); }
          })();
        }));
      } catch (error) { customShareLinks.innerHTML = `<p class="form-message error-message">${escapeHtml(error instanceof Error ? error.message : "Custom Share Links could not be loaded.")}</p>`; }
    };
    void loadCustomShareLinks();
    document.querySelector<HTMLFormElement>("#video-invitation-form")?.addEventListener("submit", (submitEvent) => {
      submitEvent.preventDefault(); const form = submitEvent.currentTarget as HTMLFormElement; const message = form.querySelector<HTMLElement>(".form-message"); const button = form.querySelector<HTMLButtonElement>("button");
      void (async () => { try { if (button) button.disabled = true; const response = await fetch(`/api/videos/${encodeURIComponent(event.videoId)}/invitations`, { method: "POST", headers: { Accept: "application/json", "Content-Type": "application/json", Origin: window.location.origin }, body: JSON.stringify({ recipientName: String(new FormData(form).get("recipientName")).trim(), recipientEmail: String(new FormData(form).get("recipientEmail")).trim() }) }); if (!response.ok) throw new Error(await responseErrorMessage(response)); const payload = await response.json() as { invitation: { url: string; expiresAt: string } }; if (message) { message.innerHTML = `<div class="custom-share-link-created"><strong>Custom Link Ready</strong><small>Expires ${escapeHtml(formatTime(payload.invitation.expiresAt))}</small><input aria-label="New Custom Share Link" readonly value="${escapeHtml(payload.invitation.url)}"><div><button class="primary-button compact copy-custom-share-link" type="button">Copy Link</button><a class="secondary-button compact" href="${escapeHtml(payload.invitation.url)}" target="_blank" rel="noopener">Open Link</a></div></div>`; const copyButton = message.querySelector<HTMLButtonElement>(".copy-custom-share-link"); copyButton?.addEventListener("click", () => { void navigator.clipboard?.writeText(payload.invitation.url).then(() => { copyButton.textContent = "Copied"; window.setTimeout(() => { copyButton.textContent = "Copy Link"; }, 2_000); }).catch(() => { copyButton.textContent = "Select the link above"; }); }); } form.reset(); await loadCustomShareLinks(); } catch (error) { if (message) message.textContent = error instanceof Error ? error.message : "The Custom Share Link could not be created."; } finally { if (button) button.disabled = false; } })();
    });
    const restoreButton = document.querySelector<HTMLButtonElement>("#restore-video");
    const archiveInstantButton = document.querySelector<HTMLButtonElement>("#archive-instant-view");
    document.querySelector<HTMLAnchorElement>("#pco-plan-link")?.addEventListener("click", (clickEvent) => {
      clickEvent.preventDefault();
      const link = clickEvent.currentTarget as HTMLAnchorElement;
      const popup = window.open(link.href, "pco_service_plan", "popup=yes,width=1180,height=860,resizable=yes,scrollbars=yes,noopener,noreferrer");
      popup?.focus();
    });
    const restorePanel = document.querySelector<HTMLElement>("#restore-progress-panel");
    const restoreProgress = document.querySelector<HTMLProgressElement>("#restore-progress");
    const restoreHeading = document.querySelector<HTMLElement>("#restore-progress-heading");
    const restoreDetail = document.querySelector<HTMLElement>("#restore-progress-detail");
    const restoreMessage = document.querySelector<HTMLElement>("#restore-message");
    let restorePollTimer: number | null = null;
    let restoreSample: { uploadedBytes: number; measuredAt: number } | null = null;
    const readRestoreStatus = async () => {
      const statusResponse = await fetch(`/api/videos/${encodeURIComponent(event.videoId)}/restore-status`, { headers: { Accept: "application/json" } });
      if (!statusResponse.ok) throw new Error(await responseErrorMessage(statusResponse));
      return statusResponse.json() as Promise<{
        mediaStatus: string;
        storageStatus: string;
        restore: null | { status: string; uploadedBytes: number; expectedSizeBytes: number };
      }>;
    };
    const pollRestoreStatus = async (): Promise<void> => {
      if (!restorePanel || !restoreProgress || !restoreHeading || !restoreDetail) return;
      restorePanel.hidden = false;
      try {
        const payload = await readRestoreStatus();
        if (payload.mediaStatus === "ready" && payload.storageStatus === "online_archived") {
          const instantVideo = document.querySelector<HTMLVideoElement>("#archive-instant-video");
          if (instantVideo) {
            const instantNotice = document.querySelector<HTMLElement>(".archive-instant-player > small");
            if (instantNotice) {
              instantNotice.innerHTML = 'Cloudflare Full Restore View is ready. <button class="text-link" id="open-full-restore" type="button">Switch views</button>';
              instantNotice.querySelector<HTMLButtonElement>("#open-full-restore")?.addEventListener("click", () => window.location.reload());
            }
            return;
          }
          window.location.reload();
          return;
        }
        const restore = payload.restore ?? { status: "preparing", uploadedBytes: 0, expectedSizeBytes: 0 };
        const measuredAt = Date.now();
        const elapsedSeconds = restoreSample ? (measuredAt - restoreSample.measuredAt) / 1_000 : 0;
        const transferredBytes = restoreSample ? restore.uploadedBytes - restoreSample.uploadedBytes : 0;
        const bytesPerSecond = elapsedSeconds > 0 && transferredBytes > 0 ? transferredBytes / elapsedSeconds : undefined;
        if (!restoreSample || transferredBytes > 0) restoreSample = { uploadedBytes: restore.uploadedBytes, measuredAt };
        const presentation = restoreProgressPresentation({ ...restore, bytesPerSecond });
        if (presentation.percent === null) restoreProgress.removeAttribute("value");
        else restoreProgress.value = presentation.percent;
        restoreHeading.textContent = presentation.heading;
        restoreDetail.textContent = presentation.detail;
        if (restoreButton) {
          restoreButton.hidden = !presentation.failed;
          restoreButton.disabled = false;
          restoreButton.textContent = "Try Cloudflare Full Restore Again";
        }
        if (restoreMessage) restoreMessage.hidden = true;
      } catch (error) {
        restoreDetail.textContent = error instanceof Error ? error.message : "Restore status is temporarily unavailable.";
      }
      restorePollTimer = window.setTimeout(() => void pollRestoreStatus(), 10_000);
    };
    const startRestore = async (): Promise<void> => {
      if (!restoreButton) return;
      if (restorePollTimer !== null) window.clearTimeout(restorePollTimer);
      restoreButton.disabled = true;
      restoreButton.hidden = false;
      restoreButton.textContent = "Starting Cloudflare Full Restore…";
      if (restorePanel) restorePanel.hidden = false;
      try {
        const restoreResponse = await fetch(`/api/videos/${encodeURIComponent(event.videoId)}/restore`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json", Origin: window.location.origin },
          body: "{}",
        });
        if (!restoreResponse.ok) throw new Error("Restore could not be started.");
        const restore = await restoreResponse.json() as { status: string };
        if (restore.status === "succeeded" || restore.status === "ready") {
          window.location.reload();
          return;
        }
        restoreButton.hidden = true;
        if (restoreMessage) restoreMessage.hidden = true;
        await pollRestoreStatus();
      } catch (error) {
        try {
          const status = await readRestoreStatus();
          if (restoreIsActive(status)) {
            restoreButton.hidden = true;
            if (restoreMessage) restoreMessage.hidden = true;
            await pollRestoreStatus();
            return;
          }
        } catch {
          // Keep the original start error when status reconciliation is also unavailable.
        }
        restoreButton.disabled = false;
        restoreButton.textContent = "Try Cloudflare Full Restore Again";
        if (restoreMessage) {
          restoreMessage.hidden = false;
          restoreMessage.textContent = error instanceof Error ? error.message : "Restore could not be started.";
        }
      }
    };
    restoreButton?.addEventListener("click", () => void startRestore());
    archiveInstantButton?.addEventListener("click", () => void (async () => {
      archiveInstantButton.disabled = true;
      archiveInstantButton.textContent = "Starting Instant View…";
      try {
        const instantResponse = await fetch(`/api/videos/${encodeURIComponent(event.videoId)}/archive-playback`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json", Origin: window.location.origin },
          body: "{}",
        });
        if (!instantResponse.ok) throw new Error(await responseErrorMessage(instantResponse));
        const instant = await instantResponse.json() as { playbackUrl: string };
        const player = document.querySelector<HTMLElement>("#event-player");
        if (!player) return;
        player.className = "stream-player archive-instant-player";
        const instantCaption = event.storageStatus === "restore_pending"
          ? "Google Archive Instant View · A previously requested Cloudflare Full Restore is still in progress."
          : "Google Archive Instant View · Cloudflare online storage is unchanged.";
        player.innerHTML = `<video id="archive-instant-video" controls playsinline preload="metadata" referrerpolicy="no-referrer" src="${escapeHtml(instant.playbackUrl)}"></video><small>${escapeHtml(instantCaption)}</small>`;
        const video = player.querySelector<HTMLVideoElement>("#archive-instant-video");
        const requestedTime = Number(new URL(window.location.href).searchParams.get("t") ?? "0");
        if (video && Number.isFinite(requestedTime) && requestedTime > 0) {
          video.addEventListener("loadedmetadata", () => { video.currentTime = requestedTime / 1_000; }, { once: true });
        }
        if (event.storageStatus === "restore_pending") void pollRestoreStatus();
      } catch (error) {
        archiveInstantButton.disabled = false;
        archiveInstantButton.textContent = "Google Archive Instant View";
        if (restoreMessage) {
          restoreMessage.hidden = false;
          restoreMessage.textContent = error instanceof Error ? error.message : "Google Archive Instant View could not be started.";
        }
      }
    })());
    if (event.storageStatus === "restore_pending") void pollRestoreStatus();
    const markerContainer = document.querySelector<HTMLElement>("#marker-container");
    const markerEditorContainer = document.querySelector<HTMLElement>("#marker-editor-container");
    const transcriptContainer = document.querySelector<HTMLElement>("#transcript-container");
    if (markerContainer && markerEditorContainer && transcriptContainer) {
      await renderIndexedMedia({
        markerContainer,
        markerEditorContainer,
        transcriptContainer,
        videoId: event.videoId,
        canEdit: Boolean(options.canEditPhase1bMetadata),
        seek: seekPlayback,
        currentPosition: currentPlaybackPosition,
        timestampSelected: () => {
          timestampPlaybackShortcutEnabled = true;
          const player = document.querySelector<HTMLElement>(".stream-player");
          const desktopPointer = window.matchMedia?.("(hover: hover) and (pointer: fine)").matches ?? true;
          if (player && desktopPointer) {
            player.tabIndex = -1;
            window.requestAnimationFrame(() => player.focus({ preventScroll: true }));
          }
        },
      });
    }
  } catch (error) {
    if (detail) detail.innerHTML = `<a class="text-link back-link" href="/">← Back to library</a><p class="error-message">${escapeHtml(error instanceof Error ? error.message : "The Event could not be loaded.")}</p>`;
  }
};
